import { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  User,
  Tag,
  Clock,
  Link2,
  Check,
} from "lucide-react";
import { getBlogPostBySlug } from "../content/blog";
import MarkdownRenderer from "../components/blog/MarkdownRenderer";
import { formatDate } from "../utils/dateUtils";
import {
  getCachedBlogContent,
  setCachedBlogContent,
  calculateReadingTime,
  getAdjacentBlogPosts,
  prefetchBlogPost,
} from "../utils/blogUtils";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ScrollToTopButton from "../components/layout/ScrollToTopButton";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Synchronously fetch post metadata (already in memory)
  const post = getBlogPostBySlug(slug);

  // Synchronously check if markdown content is already in memory cache
  const cachedContent = getCachedBlogContent(slug);
  const [content, setContent] = useState(cachedContent || "");
  const [loading, setLoading] = useState(!cachedContent);
  const [error, setError] = useState(false);

  // UX Feedback states
  const [copied, setCopied] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  // Adjacent articles for bottom navigation
  const { prevPost, nextPost } = useMemo(() => getAdjacentBlogPosts(slug), [slug]);

  // Dynamic reading time
  const readingTime = useMemo(() => {
    if (content) {
      return `${calculateReadingTime(content)} min read`;
    }
    return post?.readingTime || "3 min read";
  }, [content, post]);

  // Scroll to top immediately when slug changes
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Update dynamic document title
  useEffect(() => {
    if (post) {
      const originalTitle = document.title;
      document.title = `${post.title} | Gulshan Kumar`;
      return () => {
        document.title = originalTitle;
      };
    }
  }, [post]);

  // Track scroll depth for the reading progress bar
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight > 0) {
        const currentProgress = (window.scrollY / totalHeight) * 100;
        setReadingProgress(Math.min(100, Math.max(0, currentProgress)));
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch content if not in cache
  useEffect(() => {
    if (!post) {
      setError(true);
      setLoading(false);
      return;
    }

    // If already in state from cache, nothing more to fetch
    if (content && getCachedBlogContent(slug)) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchContent = async () => {
      try {
        const response = await fetch(post.contentPath);
        if (!response.ok) throw new Error("Post not found");
        const text = await response.text();
        if (isMounted) {
          setCachedBlogContent(slug, text);
          setContent(text);
          setLoading(false);
        }
      } catch {
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchContent();

    return () => {
      isMounted = false;
    };
  }, [slug, post, content]);

  // Handle navigation back to portfolio blog tab
  const handleBackToBlog = () => {
    navigate("/", { state: { activeTab: "blog", targetSlug: slug } });
  };

  // Handle header tab navigation from blog post page
  const handleHeaderTabChange = (tabId) => {
    navigate("/", { state: { activeTab: tabId } });
  };

  // Copy article URL to clipboard with toast
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };



  // Error / 404 state
  if (!loading && (error || !post)) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header setActiveTab={handleHeaderTabChange} />
        <div className="flex flex-col items-center justify-center min-h-screen text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-6xl font-extrabold text-gray-300 dark:text-gray-600 mb-4 font-display">
              404
            </h1>
            <p className="text-xl text-gray-500 dark:text-gray-400 mb-6">
              This blog post doesn't exist.
            </p>
            <button
              onClick={handleBackToBlog}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 cursor-pointer"
            >
              <ArrowLeft size={18} />
              Back to Blog
            </button>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 selection:bg-blue-500/20 selection:text-blue-600 dark:selection:text-blue-300">
      <Header setActiveTab={handleHeaderTabChange} />

      {/* Reading Progress Bar (Pinned beneath 70px header) */}
      <div className="fixed top-[70px] left-0 w-full h-[3px] bg-transparent z-40 pointer-events-none">
        <div
          className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 transition-all duration-100 ease-out shadow-sm"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <motion.main
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
      >
        <article>
          {/* Hero Cover Image (Renders immediately without layout shift) */}
          {post?.coverImage && (
            <div className="relative w-full max-h-[420px] overflow-hidden mt-[70px] bg-gray-100 dark:bg-gray-800">
              <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-900 via-transparent to-black/10 z-10" />
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.01]"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          {/* Content Container */}
          <div
            className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${
              post?.coverImage ? "-mt-20 relative z-20" : "pt-28"
            } pb-16`}
          >
            {/* Top Navigation Row: Back button & Share Bar */}
            <div className="flex items-center justify-between gap-4 mb-6">
              <motion.button
                onClick={handleBackToBlog}
                whileHover={{ x: -3 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 group bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-gray-200/80 dark:border-gray-700/80 shadow-xs cursor-pointer"
              >
                <ArrowLeft
                  size={16}
                  className="transition-transform duration-200 group-hover:-translate-x-0.5"
                />
                Back to Blog
              </motion.button>

              {/* Share Actions */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                {/* Copy Link Button */}
                <button
                  onClick={handleCopyLink}
                  type="button"
                  title="Copy link to article"
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
                    copied
                      ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800"
                      : "bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border-gray-200/80 dark:border-gray-700/80 hover:border-gray-300 dark:hover:border-gray-600 backdrop-blur-sm shadow-xs"
                  }`}
                  aria-label="Copy link to clipboard"
                >
                  {copied ? (
                    <>
                      <Check size={14} className="text-emerald-500" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Link2 size={14} />
                      <span className="hidden sm:inline">Copy link</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Post Header (Instantly populated from post metadata) */}
            <header className="mb-8">
              {/* Category */}
              <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 mb-4 shadow-2xs">
                {post?.category}
              </span>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight font-display mb-4 tracking-tight">
                {post?.title}
              </h1>

              {/* Meta row: Author, Date, Read Time */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-300">
                  <User size={15} className="text-blue-600 dark:text-blue-400" />
                  {post?.author}
                </span>
                <span className="inline-block w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                <span className="flex items-center gap-1.5">
                  <Calendar size={15} />
                  {post ? formatDate(post.date) : ""}
                </span>
                <span className="inline-block w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                <span className="flex items-center gap-1.5">
                  <Clock size={15} />
                  {readingTime}
                </span>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-blue-500/40 via-purple-500/40 to-transparent mt-6" />
            </header>

            {/* Markdown Body or Body-Only Skeleton */}
            <div className="min-h-[260px]">
              {loading ? (
                // Sleek body skeleton while markdown is fetching
                <div className="space-y-4 py-4 animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-md w-full skeleton-shimmer" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-md w-11/12 skeleton-shimmer" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-md w-4/5 skeleton-shimmer" />
                  <div className="h-28 bg-gray-100 dark:bg-gray-800/60 rounded-xl my-6 skeleton-shimmer" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-md w-full skeleton-shimmer" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-md w-3/4 skeleton-shimmer" />
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <MarkdownRenderer content={content} />
                </motion.div>
              )}
            </div>

            {/* Tags */}
            {post?.tags?.length > 0 && (
              <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700/80">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag size={16} className="text-gray-400" />
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Adjacent Posts Navigation (Next / Previous) */}
            {(prevPost || nextPost) && (
              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Previous Article */}
                  {prevPost ? (
                    <Link
                      to={`/blog/${prevPost.slug}`}
                      onMouseEnter={() => prefetchBlogPost(prevPost.slug)}
                      onFocus={() => prefetchBlogPost(prevPost.slug)}
                      className="group p-4 rounded-xl border border-gray-200 dark:border-gray-700/80 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-800/60 hover:shadow-md transition-all flex flex-col justify-between"
                    >
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
                        <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
                        Previous Article
                      </span>
                      <p className="text-sm font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {prevPost.title}
                      </p>
                    </Link>
                  ) : (
                    <div className="hidden sm:block" />
                  )}

                  {/* Next Article */}
                  {nextPost ? (
                    <Link
                      to={`/blog/${nextPost.slug}`}
                      onMouseEnter={() => prefetchBlogPost(nextPost.slug)}
                      onFocus={() => prefetchBlogPost(nextPost.slug)}
                      className="group p-4 rounded-xl border border-gray-200 dark:border-gray-700/80 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-800/60 hover:shadow-md transition-all flex flex-col justify-between text-right"
                    >
                      <span className="flex items-center justify-end gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-2">
                        Next Article
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </span>
                      <p className="text-sm font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {nextPost.title}
                      </p>
                    </Link>
                  ) : null}
                </div>
              </div>
            )}

            {/* Bottom CTA */}
            <div className="mt-12 text-center">
              <button
                onClick={handleBackToBlog}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white rounded-full font-medium shadow-md hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
              >
                <ArrowLeft size={18} />
                Back to all posts
              </button>
            </div>
          </div>
        </article>
      </motion.main>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
};

export default BlogPost;
